import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { FiPlay, FiClock, FiChevronRight, FiCalendar, FiExternalLink, FiLoader, FiAlertCircle } from 'react-icons/fi';
import { utcToChicago } from '../../utils/datetime-comprehensive';
import SharePointVideoPlayer from '../SharePointVideoPlayer';
import Modal from '../Modal';
import { toEmbedUrl } from '../../utils/normalizeSharePointUrl';
import apiService from '../../services/api';

const PlaylistContainer = styled.div`
  background: var(--background-secondary);
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  width: 100%;
  box-sizing: border-box;
  max-width: 100%;
  
  @media (max-width: 768px) {
    margin: 0;
    border-radius: 0;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
    width: 100%;
    max-width: 100%;
  }
`;


const WeekSection = styled.div`
  border-bottom: 1px solid var(--border-color);
  
  &:last-child {
    border-bottom: none;
  }
`;

const WeekHeader = styled.div`
  padding: 1rem 1.5rem;
  background: var(--background-primary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background-color 0.2s ease;
  min-width: 0;
  
  &:hover {
    background: var(--background-hover);
  }
`;

const WeekInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  min-width: 0;
`;

const WeekIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 999px;
  background: var(--primary-color);
  color: var(--text-inverse);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.9rem;
  flex-shrink: 0;
`;

const WeekDetails = styled.div`
  flex: 1;
`;

const WeekTitle = styled.h3`
  margin: 0 0 0.25rem 0;
  color: var(--text-primary);
  font-size: 1.1rem;
  font-weight: 500;
`;

const WeekMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.375rem;
  color: var(--text-muted);
  font-size: 0.85rem;
  
  @media (min-width: 640px) {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
  }
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const ChevronIcon = styled.div`
  color: var(--text-muted);
  transition: transform 0.2s ease;
  transform: ${props => props.$expanded ? 'rotate(90deg)' : 'rotate(0deg)'};
`;

const WeekContent = styled.div`
  max-height: ${props => props.$expanded ? '1000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

const VideoItem = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border-color);
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 1rem;
  min-height: 60px; /* Ensure minimum touch target height */
  width: 100%;
  box-sizing: border-box;
  max-width: 100%;
  overflow: hidden;
  
  &:hover {
    background: var(--background-hover);
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  &.active {
    background: var(--primary-light);
    border-left: 4px solid var(--primary-color);
  }

  &.not-ready {
    cursor: not-allowed;
    opacity: 0.65;
  }

  &.not-ready:hover {
    background: transparent;
  }

  @media (max-width: 768px) {
    padding: 12px 16px;
    gap: 12px;
    min-height: 56px;
    flex-wrap: wrap;
  }
  
  @media (max-width: 480px) {
    padding: 12px 12px;
    gap: 10px;
    min-height: 56px;
  }
`;

const VideoThumbnail = styled.div`
  width: 60px;
  height: 40px;
  background: var(--background-primary);
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
  border: 1px solid var(--border-color);
  position: relative;
  flex-shrink: 0; /* Prevent shrinking on mobile */
  
  @media (max-width: 480px) {
    width: 48px;
    height: 36px;
  }
`;

const SharePointIndicator = styled.div`
  position: absolute;
  top: -4px;
  right: -4px;
  background: var(--info-color);
  color: var(--text-inverse);
  border-radius: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  border: 2px solid var(--background-secondary);
  z-index: 1; /* Ensure it's above other elements */
  
  @media (max-width: 480px) {
    width: 18px;
    height: 18px;
    font-size: 9px;
    top: -3px;
    right: -3px;
  }
`;

const VideoInfo = styled.div`
  flex: 1 1 0;
  min-width: 0;
  max-width: calc(100% - 80px); /* Account for thumbnail width + gap */
  overflow: hidden; /* Prevent text overflow */
  
  @media (max-width: 480px) {
    max-width: calc(100% - 68px); /* Account for smaller thumbnail + gap */
  }
`;

const VideoTitle = styled.h4`
  margin: 0 0 0.25rem 0;
  color: var(--text-primary);
  font-size: 0.95rem;
  font-weight: 500;
  min-width: 0;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  max-width: 100%;
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    line-height: 1.2;
  }
`;

const VideoDescription = styled.p`
  margin: 0;
  color: var(--text-muted);
  font-size: 0.8rem;
  line-height: 1.3;
  overflow-wrap: anywhere;
  word-break: break-word;
  min-width: 0;
`;

// duration styling removed (no longer used)

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const ProcessingPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3125rem;
  margin-top: 0.25rem;
  padding: 0.1875rem 0.5rem;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  width: fit-content;

  ${(p) => p.$status === 'processing' && `
    background: rgba(184, 149, 106, 0.12);
    color: var(--primary-color);
  `}

  ${(p) => p.$status === 'failed' && `
    background: rgba(220, 38, 38, 0.12);
    color: #b91c1c;
  `}

  svg.spinning {
    animation: ${spin} 1.2s linear infinite;
  }
`;

// Small badge shown next to the week summary (collapsed view) when the week
// contains any non-ready recordings. Only renders for admin/instructor/granted
// viewers — students never have non-ready recordings in their list.
const WeekStatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3125rem;
  padding: 0.1875rem 0.5rem;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  flex-shrink: 0;

  ${(p) => p.$status === 'processing' && `
    background: rgba(184, 149, 106, 0.12);
    color: var(--primary-color);
  `}

  ${(p) => p.$status === 'failed' && `
    background: rgba(220, 38, 38, 0.12);
    color: #b91c1c;
  `}

  svg.spinning {
    animation: weekSpin 1.2s linear infinite;
  }

  @keyframes weekSpin {
    to { transform: rotate(360deg); }
  }
`;

// Compute a single aggregated status for a list of recordings.
// Returns { status: 'ready'|'processing'|'failed', label, count, etaLabel? }.
function aggregateWeekStatus(recordings) {
  let processingCount = 0;
  let failedCount = 0;
  for (const r of recordings || []) {
    const status = r?.processing_status
      || r?.video_variants?.processing_status
      || 'ready';
    if (status === 'processing') processingCount++;
    else if (status === 'failed') failedCount++;
  }
  if (failedCount > 0) return { status: 'failed', count: failedCount };
  if (processingCount > 0) {
    const placeholder = recordings.find(r => {
      const st = r?.processing_status || 'ready';
      return st === 'processing' && (r?.processing_kind === 'merge-upload');
    });
    let label = 'Processing';
    let etaLabel = null;
    if (placeholder) {
      const stage = placeholder.processing_stage;
      if (stage === 'uploading') label = 'Uploading';
      else if (stage === 'converting') label = 'Converting';
      else if (stage === 'merging') label = 'Merging';
      const etaSec = computeEtaSeconds({
        stage,
        stageStartedAt: placeholder.stage_started_at,
        totalDurationSec: placeholder.total_duration_sec,
        expectedSourceCount: placeholder.expected_source_count,
      });
      etaLabel = formatEta(etaSec);
    }
    return { status: 'processing', count: processingCount, label, etaLabel };
  }
  return { status: 'ready', count: 0 };
}

// Inline badge that reads recording.processing_status, polls the status
// endpoint while 'processing', and hides itself once 'ready'.
// The status now arrives as a top-level field on the recording row (the
// backend extracts it from video_variants metadata). Keep the older nested
// path as a fallback for any in-flight clients during deploy.
// ETA model for merge-upload stages (multipliers vs. total input duration).
// Tuned for MediaConvert on-demand queue at QVBR 8 / 1080p.
//   - Convert stage: sources convert in parallel, so stage time ≈
//     1.5 × (average source duration). Average ≈ total / count.
//   - Merge stage:   the merge job re-encodes the concatenated stream
//                    end-to-end, so ≈ 1.0 × total duration.
const STAGE_FACTORS = {
  converting: { mult: 1.5, perSource: true },
  merging:    { mult: 1.0, perSource: false },
};

const computeEtaSeconds = ({ stage, stageStartedAt, totalDurationSec, expectedSourceCount }) => {
  if (!stage || !stageStartedAt || !totalDurationSec) return null;
  const factor = STAGE_FACTORS[stage];
  if (!factor) return null;
  const baseSec = factor.perSource && expectedSourceCount
    ? totalDurationSec / Math.max(1, expectedSourceCount)
    : totalDurationSec;
  const expectedTotal = factor.mult * baseSec;
  const elapsed = (Date.now() - new Date(stageStartedAt).getTime()) / 1000;
  const remaining = Math.max(30, expectedTotal - elapsed);
  return Math.round(remaining);
};

const formatEta = (sec) => {
  if (sec == null || sec <= 0) return null;
  if (sec < 60) return '<1m left';
  const mins = Math.round(sec / 60);
  if (mins < 60) return `~${mins}m left`;
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem === 0 ? `~${hrs}h left` : `~${hrs}h ${rem}m left`;
};

function ProcessingBadge({ recording }) {
  const initial = recording?.processing_status
    || recording?.video_variants?.processing_status
    || recording?.videoVariants?.processing_status
    || 'ready';
  const [status, setStatus] = useState(initial);
  const [stage, setStage] = useState(recording?.processing_stage || null);
  const [kind, setKind] = useState(recording?.processing_kind || null);
  const [stageStartedAt, setStageStartedAt] = useState(recording?.stage_started_at || null);
  const [totalDurationSec, setTotalDurationSec] = useState(recording?.total_duration_sec || null);
  const [expectedSourceCount, setExpectedSourceCount] = useState(recording?.expected_source_count || null);
  const [, forceTick] = useState(0); // bump every 30s so ETA decrements
  const stopped = useRef(false);

  useEffect(() => {
    if (status !== 'processing' || !recording?.id) return undefined;
    stopped.current = false;
    let timer = null;

    const tick = async () => {
      if (stopped.current) return;
      try {
        const res = await apiService.getRecordingProcessingStatus(recording.id);
        if (stopped.current) return;
        if (res?.success) {
          if (res.stage && res.stage !== stage) setStage(res.stage);
          if (res.kind && res.kind !== kind) setKind(res.kind);
          if (res.stageStartedAt && res.stageStartedAt !== stageStartedAt) setStageStartedAt(res.stageStartedAt);
          if (res.totalDurationSec && res.totalDurationSec !== totalDurationSec) setTotalDurationSec(res.totalDurationSec);
          if (res.expectedSourceCount && res.expectedSourceCount !== expectedSourceCount) setExpectedSourceCount(res.expectedSourceCount);
          if (res.status && res.status !== status) {
            setStatus(res.status);
            if (res.status === 'ready' || res.status === 'failed') return;
          }
        }
      } catch (e) {
        // Silent — keep polling. Network blips shouldn't stop the badge.
      }
      timer = setTimeout(tick, 5000);
    };
    timer = setTimeout(tick, 3000);

    // ETA counter — re-render every 30s while processing so the displayed
    // "~12m left" decrements without needing a server round-trip.
    const etaTicker = setInterval(() => forceTick(n => n + 1), 30000);

    return () => {
      stopped.current = true;
      if (timer) clearTimeout(timer);
      clearInterval(etaTicker);
    };
  }, [status, stage, kind, stageStartedAt, totalDurationSec, expectedSourceCount, recording?.id]);

  if (status === 'ready') return null;
  if (status === 'processing') {
    // Stage-specific text for merge-uploads; default for single-file conversion.
    let label = 'Processing — ready shortly';
    if (kind === 'merge-upload') {
      if (stage === 'uploading') label = 'Uploading';
      else if (stage === 'converting') label = 'Converting';
      else if (stage === 'merging') label = 'Merging';
    }
    const etaSec = computeEtaSeconds({ stage, stageStartedAt, totalDurationSec, expectedSourceCount });
    const etaLabel = formatEta(etaSec);
    return (
      <ProcessingPill $status="processing" title="Recording is processing. It will appear to students once ready.">
        <FiLoader size={11} className="spinning" />
        {label}{etaLabel ? ` · ${etaLabel}` : ''}
      </ProcessingPill>
    );
  }
  if (status === 'failed') {
    return (
      <ProcessingPill $status="failed" title={recording?.processing_error || 'Conversion failed'}>
        <FiAlertCircle size={11} />
        {kind === 'merge-upload' ? 'Merge failed' : 'Conversion failed'}
      </ProcessingPill>
    );
  }
  return null;
}

const EmptyState = styled.div`
  padding: 3rem 1.5rem;
  text-align: center;
  color: var(--text-muted);
`;

const EmptyIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
`;

function VideoPlaylist({ recordings, onVideoSelect, selectedVideo }) {
  const [expandedWeeks, setExpandedWeeks] = useState(new Set());
  const [sharePointModalOpen, setSharePointModalOpen] = useState(false);
  const [activeSharePointVideo, setActiveSharePointVideo] = useState(null);

  const parseDurationToSeconds = (duration) => {
    const raw = String(duration || '').trim();
    if (!raw) return 0;

    if (/^\d+(?::\d{1,2}){1,2}$/.test(raw)) {
      const parts = raw.split(':').map(Number);
      if (parts.length === 2) {
        return (parts[0] * 3600) + (parts[1] * 60);
      }
      return (parts[0] * 3600) + (parts[1] * 60) + parts[2];
    }

    const hours = Number(raw.match(/(\d+)\s*h/i)?.[1] || 0);
    const minutes = Number(raw.match(/(\d+)\s*m/i)?.[1] || 0);
    const seconds = Number(raw.match(/(\d+)\s*s/i)?.[1] || 0);
    if (hours || minutes || seconds) {
      return (hours * 3600) + (minutes * 60) + seconds;
    }

    if (/^\d+$/.test(raw)) {
      return Number(raw) * 60;
    }

    return 0;
  };

  // Hide any recording that hasn't finished converting yet — admins/instructors
  // can still see and monitor it via Manage Course Content → Recordings, but
  // the public playlist should only surface playable, ready files. Also hide
  // the transient merge-source rows that the server returns during a multi-file
  // upload pipeline (they aren't independently playable).
  const playableRecordings = recordings.filter((r) => {
    const status = r?.processing_status
      || r?.video_variants?.processing_status
      || r?.videoVariants?.processing_status
      || 'ready';
    const kind = r?.processing_kind
      || r?.video_variants?.kind
      || r?.videoVariants?.kind
      || null;
    if (kind === 'merge-source') return false;
    return status === 'ready';
  });

  // Group recordings by week
  const recordingsByWeek = playableRecordings.reduce((acc, recording) => {
    const week = recording.week || 1;
    if (!acc[week]) {
      acc[week] = [];
    }
    acc[week].push(recording);
    return acc;
  }, {});

  // Sort weeks and recordings within each week
  const sortedWeeks = Object.keys(recordingsByWeek)
    .map(Number)
    .sort((a, b) => a - b);

  sortedWeeks.forEach(week => {
    recordingsByWeek[week].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  });

  const toggleWeek = (week) => {
    const newExpanded = new Set(expandedWeeks);
    if (newExpanded.has(week)) {
      newExpanded.delete(week);
    } else {
      newExpanded.add(week);
    }
    setExpandedWeeks(newExpanded);
  };

  const isRecordingReady = (recording) => {
    const status = recording?.processing_status
      || recording?.video_variants?.processing_status
      || recording?.videoVariants?.processing_status
      || 'ready';
    return status === 'ready';
  };

  const handleVideoClick = (recording) => {
    // Don't try to play recordings whose conversion isn't finished yet.
    if (!isRecordingReady(recording)) return;
    // Check if this recording has SharePoint links
    if (recording.spEmbedUrl) {
      setActiveSharePointVideo(recording);
      setSharePointModalOpen(true);
    } else if (onVideoSelect) {
      onVideoSelect(recording);
    }
  };


  const closeSharePointModal = () => {
    setSharePointModalOpen(false);
    setActiveSharePointVideo(null);
  };

  if (playableRecordings.length === 0) {
    // Distinguish "nothing uploaded yet" from "uploads in progress but not yet ready"
    // so admins watching their own upload know it's coming, not missing.
    const hasProcessing = recordings.some((r) => {
      const status = r?.processing_status
        || r?.video_variants?.processing_status
        || r?.videoVariants?.processing_status
        || 'ready';
      return status === 'processing';
    });
    return (
      <EmptyState>
        <EmptyIcon>🎥</EmptyIcon>
        <h3>No recordings available</h3>
        <p>
          {hasProcessing
            ? 'A recording is still being processed. It will appear here once conversion finishes.'
            : 'Recordings will appear here once they are added to the course.'}
        </p>
      </EmptyState>
    );
  }

  return (
    <>
      <PlaylistContainer>

      {sortedWeeks.map(week => {
        const weekRecordings = recordingsByWeek[week];
        const isExpanded = expandedWeeks.has(week);
        const totalDuration = weekRecordings.reduce((total, recording) => total + parseDurationToSeconds(recording.duration), 0);

        const formatDuration = (seconds) => {
          const hours = Math.floor(seconds / 3600);
          const minutes = Math.floor((seconds % 3600) / 60);
          const remainingSeconds = Math.floor(seconds % 60);
          if (hours > 0) {
            return `${hours}h ${minutes}m${remainingSeconds > 0 ? ` ${remainingSeconds}s` : ''}`;
          }
          return `${minutes}m${remainingSeconds > 0 ? ` ${remainingSeconds}s` : ''}`;
        };

        const formatChicagoDate = (iso) => {
          try {
            if (!iso) return '';
            // Prefer the date portion only; treat it as the intended wall-date
            const datePart = typeof iso === 'string' ? iso.slice(0, 10) : '';
            if (datePart) {
              const parts = datePart.split('-');
              const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
              return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
            }
            const dt = utcToChicago(iso);
            // Render date only (no time)
            return dt.toFormat('MMM d, yyyy');
          } catch (e) {
            return '';
          }
        };

        return (
          <WeekSection key={week}>
            <WeekHeader onClick={() => toggleWeek(week)}>
              <WeekInfo>
                <WeekIcon>W{week}</WeekIcon>
                <WeekDetails>
                  <WeekTitle>Week {week}</WeekTitle>
                  <WeekMeta>
                    <MetaItem>
                      <FiPlay size={14} />
                      {weekRecordings.length} recording{weekRecordings.length !== 1 ? 's' : ''}
                    </MetaItem>
                    <MetaItem>
                      <FiClock size={14} />
                      {totalDuration > 0 ? formatDuration(totalDuration) : '0m'}
                    </MetaItem>
                    <MetaItem>
                      <FiCalendar size={14} />
                      {weekRecordings[0]?.recording_date ? formatChicagoDate(weekRecordings[0].recording_date) : 'No date'}
                    </MetaItem>
                    {(() => {
                      const agg = aggregateWeekStatus(weekRecordings);
                      if (agg.status === 'ready') return null;
                      if (agg.status === 'processing') {
                        return (
                          <WeekStatusPill $status="processing" onClick={(e) => e.stopPropagation()} title="One or more recordings are still being processed">
                            <FiLoader size={10} className="spinning" />
                            {agg.label || 'Processing'}{agg.etaLabel ? ` · ${agg.etaLabel}` : ''}
                          </WeekStatusPill>
                        );
                      }
                      return (
                        <WeekStatusPill $status="failed" onClick={(e) => e.stopPropagation()} title="One or more recordings failed to process">
                          <FiAlertCircle size={10} />
                          Failed
                        </WeekStatusPill>
                      );
                    })()}
                  </WeekMeta>
                </WeekDetails>
              </WeekInfo>
              <ChevronIcon $expanded={isExpanded}>
                <FiChevronRight size={20} />
              </ChevronIcon>
            </WeekHeader>

            <WeekContent $expanded={isExpanded}>
              {weekRecordings.map((recording, index) => {
                const ready = isRecordingReady(recording);
                const classes = [
                  selectedVideo?.id === recording.id ? 'active' : '',
                  ready ? '' : 'not-ready',
                ].filter(Boolean).join(' ');
                return (
                <VideoItem
                  key={recording.id}
                  className={classes}
                  onClick={() => handleVideoClick(recording)}
                >
                  <VideoThumbnail>
                    <FiPlay size={20} />
                    {recording.spEmbedUrl && (
                      <SharePointIndicator title="SharePoint Video">
                        <FiExternalLink size={10} />
                      </SharePointIndicator>
                    )}
                  </VideoThumbnail>
                  <VideoInfo>
                    <VideoTitle>{recording.title}</VideoTitle>
                    {recording.description && recording.description.trim() && recording.description.trim() !== '<p></p>' && (
                      <VideoDescription>{recording.description}</VideoDescription>
                    )}
                    <ProcessingBadge recording={recording} />
                  </VideoInfo>
                  {/* Duration removed per design request */}
                </VideoItem>
                );
              })}
            </WeekContent>
          </WeekSection>
        );
      })}
    </PlaylistContainer>

    {/* SharePoint Video Modal */}
    <Modal 
      open={sharePointModalOpen} 
      onClose={closeSharePointModal} 
      title={activeSharePointVideo?.title || "Course Recording"}
    >
      {activeSharePointVideo && (
        <SharePointVideoPlayer
          spEmbedUrl={toEmbedUrl(activeSharePointVideo.spEmbedUrl)}
          title={activeSharePointVideo.title}
        />
      )}
    </Modal>
  </>
  );
}

export default VideoPlaylist;
